'use client';

import React, { useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── NAMES OF ALLAH QUICK-SELECT ─────────────────────────────────────────────
const NAMES_QUICK_SELECT = [
  { arabic: 'الخالق', transliteration: 'Al-Khaliq', meaning: 'The Creator' },
  { arabic: 'الرزاق', transliteration: 'Ar-Razzaq', meaning: 'The Ever-Provider' },
  { arabic: 'المحيي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life' },
  { arabic: 'اللطيف', transliteration: 'Al-Latif', meaning: 'The Subtle' },
  { arabic: 'الحكيم', transliteration: 'Al-Hakim', meaning: 'The All-Wise' },
  { arabic: 'الرحمن', transliteration: 'Ar-Rahman', meaning: 'The Most Compassionate' },
  { arabic: 'النور', transliteration: 'An-Nur', meaning: 'The Light' },
  { arabic: 'الباري', transliteration: 'Al-Bari', meaning: 'The Evolver' },
  { arabic: 'المصور', transliteration: 'Al-Musawwir', meaning: 'The Fashioner' },
  { arabic: 'الحي', transliteration: 'Al-Hayy', meaning: 'The Ever-Living' },
  { arabic: 'القيوم', transliteration: 'Al-Qayyum', meaning: 'The Sustainer' },
  { arabic: 'الجميل', transliteration: 'Al-Jamil', meaning: 'The Beautiful' },
];

const BIOMES = [
  { value: 'forest', label: '🌲 Forest' },
  { value: 'desert', label: '🏜 Desert' },
  { value: 'coastal', label: '🌊 Coastal' },
  { value: 'urban', label: '🏙 Urban' },
  { value: 'watershed', label: '💧 Watershed' },
  { value: 'garden', label: '🌱 Garden' },
  { value: 'sky', label: '☁️ Sky' },
  { value: 'other', label: '◎ Other' },
];

const VISIBILITY_OPTIONS = [
  { value: 'private', label: '🔒 Private', desc: 'Only you can see this' },
  { value: 'community', label: '👥 Community', desc: 'Visible to logged-in caretakers after approval' },
  { value: 'public', label: '🌍 Public', desc: 'Visible to all after guardian approval' },
];

interface SelectedName {
  arabic: string;
  transliteration: string;
  meaning: string;
  resonanceNote: string;
}

interface ObservationSubmissionPanelProps {
  onClose?: () => void;
  linkedSectionNumber?: number;
}

export default function ObservationSubmissionPanel({ onClose, linkedSectionNumber }: ObservationSubmissionPanelProps) {
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [reflectionText, setReflectionText] = useState('');
  const [biome, setBiome] = useState('other');
  const [locationLabel, setLocationLabel] = useState('');
  const [speciesText, setSpeciesText] = useState('');
  const [quranicCitation, setQuranicCitation] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [selectedNames, setSelectedNames] = useState<SelectedName[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [resonanceInput, setResonanceInput] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const toggleName = (name: typeof NAMES_QUICK_SELECT[0]) => {
    const exists = selectedNames.find(n => n.transliteration === name.transliteration);
    if (exists) {
      setSelectedNames(prev => prev.filter(n => n.transliteration !== name.transliteration));
    } else {
      setSelectedNames(prev => [...prev, { ...name, resonanceNote: '' }]);
    }
  };

  const updateResonance = (transliteration: string, note: string) => {
    setSelectedNames(prev => prev.map(n =>
      n.transliteration === transliteration ? { ...n, resonanceNote: note } : n
    ));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !reflectionText.trim()) {
      setError('Please provide a title and reflection text.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      let photoUrl: string | null = null;
      let photoStoragePath: string | null = null;

      // Upload photo if provided
      if (photoFile && user) {
        const ext = photoFile.name.split('.').pop();
        const path = `observations/${user.id}/${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('ecosystem-observations')
          .upload(path, photoFile, { upsert: false });

        if (!uploadError && uploadData) {
          photoStoragePath = uploadData.path;
          const { data: urlData } = supabase.storage
            .from('ecosystem-observations')
            .getPublicUrl(uploadData.path);
          photoUrl = urlData?.publicUrl || null;
        }
      }

      const speciesArray = speciesText
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const namesArray = selectedNames.map(n => n.transliteration);

      const { data: obsData, error: obsError } = await supabase
        .from('ecosystem_observations')
        .insert({
          user_id: user?.id || null,
          title: title.trim(),
          reflection_text: reflectionText.trim(),
          photo_url: photoUrl,
          photo_storage_path: photoStoragePath,
          biome,
          location_label: locationLabel.trim(),
          species_identified: speciesArray,
          linked_names: namesArray,
          quranic_citation: quranicCitation.trim(),
          letter_section_ids: linkedSectionNumber ? [linkedSectionNumber] : [],
          observation_status: 'pending',
          gallery_visibility: visibility,
        })
        .select()
        .single();

      if (obsError) throw obsError;

      // Insert name links
      if (obsData && selectedNames.length > 0) {
        const nameLinks = selectedNames.map(n => ({
          observation_id: obsData.id,
          name_arabic: n.arabic,
          name_transliteration: n.transliteration,
          name_meaning: n.meaning,
          resonance_note: n.resonanceNote,
        }));
        await supabase.from('observation_names_links').insert(nameLinks);
      }

      setSubmitted(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Submission failed. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: 'rgba(15,30,24,0.95)', border: '1px solid rgba(126,200,160,0.3)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🌱</div>
        <h3 style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: '#7ec8a0', marginBottom: 8 }}>Observation Submitted</h3>
        <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: 'rgba(245,240,232,0.7)', lineHeight: 1.8, marginBottom: 20 }}>
          Your observation has been submitted for community guardian review. Alhamdulilah.
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(245,240,232,0.4)', letterSpacing: '.1em' }}>
          VISIBILITY: {visibility.toUpperCase()} · STATUS: PENDING APPROVAL
        </p>
        {onClose && (
          <button onClick={onClose} style={{ marginTop: 20, padding: '8px 20px', background: 'rgba(126,200,160,0.15)', border: '1px solid rgba(126,200,160,0.3)', borderRadius: 6, color: '#7ec8a0', fontFamily: 'monospace', fontSize: 10, cursor: 'pointer', letterSpacing: '.1em' }}>
            CLOSE
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: 'rgba(10,20,16,0.98)', border: '1px solid rgba(44,74,62,0.4)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(44,74,62,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', color: 'rgba(201,168,76,0.5)', textTransform: 'uppercase', marginBottom: 4 }}>
            ECOSYSTEM JOURNEY
          </p>
          <h3 style={{ fontFamily: '"Amiri", serif', fontSize: 20, color: '#c9a84c', margin: 0 }}>
            Submit Observation
          </h3>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(245,240,232,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        )}
      </div>

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxHeight: '70vh', overflowY: 'auto' }}>

        {/* Photo Upload */}
        <div>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Photo (Optional)
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '1px dashed rgba(44,74,62,0.5)',
              borderRadius: 8,
              padding: photoPreview ? 0 : '24px',
              textAlign: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              minHeight: photoPreview ? 160 : 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(15,30,24,0.5)',
            }}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="Preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }} />
            ) : (
              <div>
                <p style={{ fontSize: 24, marginBottom: 8 }}>📷</p>
                <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(245,240,232,0.4)' }}>Tap to upload a photo of creation</p>
              </div>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
        </div>

        {/* Title */}
        <div>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
            Title *
          </label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Mycorrhizal network at the park edge"
            style={{
              width: '100%',
              padding: '10px 14px',
              background: 'rgba(15,30,24,0.8)',
              border: '1px solid rgba(44,74,62,0.4)',
              borderRadius: 6,
              color: '#f5f0e8',
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Biome + Location */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              Biome
            </label>
            <select
              value={biome}
              onChange={e => setBiome(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.4)', borderRadius: 6, color: '#f5f0e8', fontFamily: '"DM Sans", sans-serif', fontSize: 13, outline: 'none' }}
            >
              {BIOMES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              Location
            </label>
            <input
              value={locationLabel}
              onChange={e => setLocationLabel(e.target.value)}
              placeholder="e.g. Lincoln Park, Chicago"
              style={{ width: '100%', padding: '10px 14px', background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.4)', borderRadius: 6, color: '#f5f0e8', fontFamily: '"DM Sans", sans-serif', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Reflection Text */}
        <div>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
            Reflection *
          </label>
          <textarea
            value={reflectionText}
            onChange={e => setReflectionText(e.target.value)}
            placeholder="Describe what you observed and how it points to the Names and Attributes of Allah..."
            rows={5}
            style={{
              width: '100%',
              padding: '10px 14px',
              background: 'rgba(15,30,24,0.8)',
              border: '1px solid rgba(44,74,62,0.4)',
              borderRadius: 6,
              color: '#f5f0e8',
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              lineHeight: 1.8,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Names of Allah */}
        <div>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Linked Names of Allah
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {NAMES_QUICK_SELECT.map(name => {
              const isSelected = selectedNames.some(n => n.transliteration === name.transliteration);
              return (
                <button
                  key={name.transliteration}
                  onClick={() => toggleName(name)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 20,
                    border: `1px solid ${isSelected ? 'rgba(201,168,76,0.5)' : 'rgba(44,74,62,0.4)'}`,
                    background: isSelected ? 'rgba(201,168,76,0.12)' : 'transparent',
                    color: isSelected ? '#c9a84c' : 'rgba(245,240,232,0.5)',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 11,
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                  }}
                >
                  {name.transliteration}
                </button>
              );
            })}
          </div>
          {selectedNames.map(name => (
            <div key={name.transliteration} style={{ marginBottom: 8 }}>
              <label style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.5)', display: 'block', marginBottom: 4 }}>
                {name.arabic} {name.transliteration} — Resonance note
              </label>
              <input
                value={name.resonanceNote}
                onChange={e => updateResonance(name.transliteration, e.target.value)}
                placeholder={`How does this observation reflect ${name.meaning}?`}
                style={{ width: '100%', padding: '8px 12px', background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 6, color: '#f5f0e8', fontFamily: '"DM Sans", sans-serif', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}
        </div>

        {/* Species + Quranic Citation */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              Species / Elements Identified (comma-separated)
            </label>
            <input
              value={speciesText}
              onChange={e => setSpeciesText(e.target.value)}
              placeholder="e.g. Oak tree, Mycorrhizal fungi, Robin"
              style={{ width: '100%', padding: '10px 14px', background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.4)', borderRadius: 6, color: '#f5f0e8', fontFamily: '"DM Sans", sans-serif', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              Quranic Citation (Optional)
            </label>
            <input
              value={quranicCitation}
              onChange={e => setQuranicCitation(e.target.value)}
              placeholder="e.g. Quran 6:99"
              style={{ width: '100%', padding: '10px 14px', background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.4)', borderRadius: 6, color: '#f5f0e8', fontFamily: '"DM Sans", sans-serif', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Gallery Visibility */}
        <div>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', color: 'rgba(201,168,76,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Gallery Visibility
          </label>
          <div className="flex gap-2 flex-wrap">
            {VISIBILITY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setVisibility(opt.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: `1px solid ${visibility === opt.value ? 'rgba(201,168,76,0.5)' : 'rgba(44,74,62,0.3)'}`,
                  background: visibility === opt.value ? 'rgba(201,168,76,0.1)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 150ms ease',
                }}
              >
                <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: visibility === opt.value ? '#c9a84c' : 'rgba(245,240,232,0.6)', margin: 0 }}>{opt.label}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(245,240,232,0.3)', margin: 0, marginTop: 2 }}>{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: '10px 14px', background: 'rgba(224,82,82,0.1)', border: '1px solid rgba(224,82,82,0.3)', borderRadius: 6 }}>
            <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#e05252', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            padding: '12px 24px',
            background: submitting ? 'rgba(45,122,71,0.2)' : 'rgba(45,122,71,0.3)',
            border: '1px solid rgba(45,122,71,0.5)',
            borderRadius: 8,
            color: '#7ec8a0',
            fontFamily: 'monospace',
            fontSize: 11,
            letterSpacing: '.15em',
            textTransform: 'uppercase',
            cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'all 200ms ease',
          }}
        >
          {submitting ? 'Submitting...' : '🌱 Submit for Guardian Review'}
        </button>

        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(245,240,232,0.3)', textAlign: 'center', letterSpacing: '.05em' }}>
          Observations are reviewed by community guardians before appearing in the gallery.
        </p>
      </div>
    </div>
  );
}
